import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";
import { statsSchema } from "../../../common/statsSchema";
import { tagsSchema, tagsEnhancedSchema } from "../../../common/tagsSchema";

const productAdStatsSchema = z.object({
  conversion_same_sku: z.number().nullable(),
  conversion_same_sku_prev: z.number().nullable(),
  conversion_same_sku_delta: z.number().nullable(),
  conversion_same_sku_percent: z.number().nullable(),

  roms: z.number().nullable(),
  roms_prev: z.number().nullable(),
  roms_delta: z.number().nullable(),
  roms_percent: z.number().nullable(),
});

export const productAdApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4ProductAds",
    method: "get",
    path: "/v4/query/?table=product_ad&grouping=product_advertisement_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            product_advertisement_id: z.number(),
            gvproduct_id: z.number(),
            gvadvertisement_id: z.number(),
            ad_name: z.string(),
            ad_type: z.number(),
            product_title: z.string(),
            asin: z.string(),
            sku: z.string(),
            sales_state: z.string(),
            inventory_state: z.string(),
            stock_status: z.string(),
            product_price: z.number(),
            user_status: z.string(),
            serving_status: z.string(),
            ad_status: z.number(),
            state: z.number(),
            tags: tagsSchema,
            tags_enhanced: tagsEnhancedSchema,
          })
          .merge(statsSchema)
          .merge(productAdStatsSchema),
      ),
      cursor: cursorSchema,
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4ProductAdReportByDate",
      method: "get",
      path: "/v4/query/?table=product_ad&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(productAdStatsSchema),
        ),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductAdReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=product_ad",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(productAdStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductAdReportExport",
      method: "get",
      path: "/v4/query/?table=product_ad&xls=true&report_type=product_ad_report",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
        {
          name: "advertisement_id",
          type: "Query",
          schema: z.number().optional(),
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
